import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// Environment-aware base URL, shared with the breadcrumb structured data in common/ui.js.
export const getBaseUrl = () => {
  if (process.env.REACT_APP_ENV === 'production') {
    return 'https://frothtestops.com';
  } else if (process.env.REACT_APP_ENV === 'uat') {
    return 'https://dev.frothtestops.com';
  } else if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://staging.frothtestops.com';
  } else if (process.env.REACT_APP_ENV === 'test') {
    return 'https://test.frothtestops.com';
  }
  return 'https://dev.frothtestops.com'; // Default to dev (UAT environment)
};

/**
 * Reusable SEO component for consistent meta tags across all pages
 * Handles Open Graph, Twitter Cards, and other essential meta tags for social sharing
 */
const SEOHead = ({
  title = 'Top Test Management Tools for Smarter QA & Projects',
  description = 'Discover top test management tools that simplify QA workflows, enhance teamwork, boost test coverage, and help deliver faster, high-quality projects.',
  keywords = 'test management, QA tools, software testing, test automation, Froth TestOps',
  ogImage = '/og-image.png',
  ogType = 'website',
  canonicalUrl,
  noIndex = false,
  structuredData = null
}) => {
  const location = useLocation();

  // Build full URL for Open Graph
  const baseUrl = getBaseUrl();
  // Lower-case so /Integration and /integration share one canonical URL.
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname.toLowerCase()}`;
  const fullOgImageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Robots meta tag for SEO control */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical URL (omitted for noindex pages such as the 404) */}
      {!noIndex && <link rel="canonical" href={fullUrl} />}

      {/* Open Graph Meta Tags for Facebook, LinkedIn, Teams, etc. */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Froth TestOps" />

      {/* Additional Open Graph tags for better Teams support */}
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImageUrl} />
      <meta name="twitter:site" content="@frothtestops" />

      {/* Additional meta tags for better social sharing */}
      <meta name="theme-color" content="#3a6ab6" />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;